import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { connectHitInsights } from 'react-instantsearch-dom';

import Button from '../../atoms/button';
import Price from './product-card-price';
import ProductBadge from '../../molecules/product-badge';
import QuickShop from './product-card-quickshop';
import WishlistButton from './product-card-wishlist';

import { aa } from '../../utils/algolia';
import { useSearch } from '../../utils/context/search-provider';
import { logAmplitude } from '../../utils/amplitude';
import parseSlugFromProductUrl from '../../utils/parseSlugFromProductUrl';

const CardPrice = styled(Price)``;

const Wishlist = styled(WishlistButton)``;

const CardTitle = styled.h2(() => ({
  lineHeight: 1.4,
  marginBottom: '0.5rem'
}));

const CardBrand = styled.h2(() => ({
  fontStyle: 'italic',
  lineHeight: 1.1,
  marginBottom: '0.5rem'
}));

const Image = styled.img`
  left: 0;
  max-width: 100%;
  opacity: 1;
  position: absolute;
  top: 0;
  width: 100%;
  transition: opacity ${(props) => props.theme.animation.slow} ${(props) => props.theme.animation.easeOutQuad};
`;

const ImageWrapper = styled.figure`
  margin-bottom: ${({ showQuickShop }) => (showQuickShop ? '3rem' : 0)};
  overflow: hidden;
  padding-top: 100%;
  position: relative;
  text-align: center;

  ${Image} + ${Image} {
    opacity: 0;
    pointer-events: none;
  }

  ${(props) => (
    props.hasSideImage
      ? css`
        :hover {
          ${Image} {
            opacity: 0;

            + ${Image} {
              opacity: 1;
            }
          }
        }
      ` : ''
  )}

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    margin-bottom: ${({ showQuickShop }) => (showQuickShop ? '4rem' : 0)};
  }
`;

const QuickShopButton = styled(Button)(({ theme }) => ({
  margin: '0.5rem auto 0 auto',
  display: 'none',
  height: '3rem',
  lineHeight: 'calc(3rem - 6px)',
  opacity: 0,
  transition: `opacity ${theme.animation.slow} ${theme.animation.easeOutQuad}`,
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    display: 'flex'
  }
}));

const CardAnchor = styled.a`
  text-decoration: none;
`;

const handleOnImageError = (e) => {
  const img = e.target;
  img.src = `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default-image.jpg`;
};

const Card = styled.article(({ theme }) => ({
  position: 'relative',
  cursor: 'pointer',
  header: {
    alignItems: 'center',
    color: theme.color.brand,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.font.serif,
    letterSpacing: 0.5,
    marginBottom: '1.5rem',
    marginTop: '1rem',
    textAlign: 'center',
    [CardBrand]: { order: 1 },
    [CardTitle]: { order: 2 },
    [CardPrice]: { order: 3 }
  },
  '&.--pulse': {
    animation: `${theme.animations.pulse} ${theme.animation.default}`
  },
  '> a': {
    display: 'block',
    textDecoration: 'none'
  },
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    ':hover': {
      '> a > button': { opacity: 1 },
      [Wishlist]: { opacity: 1 },
      [QuickShopButton]: { opacity: 1 }
    }
  }
}));

const ProductCard = ({
  hit,
  activeQuickShopID,
  setActiveQuickshopID,
  isWishlistEnabled,
  insights
}) => {
  const ref = useRef();
  const router = useRouter();
  const { updateAlgoliaSearchParams } = useSearch();

  const onCardClick = (e, params) => {
    const { objectID, queryID } = params;

    const { target } = e;

    const isQuickShopButton = target.dataset.id === 'quickshop';

    updateAlgoliaSearchParams({ objectID, queryID });
    insights('clickedObjectIDsAfterSearch', params);
    const eventProps = {
      product: {
        ...hit,
        category: hit.product_type?.[0],
        sku: hit.manufacturer_id
      },
      // eslint-disable-next-line no-underscore-dangle
      position: hit.__position,
      list: 'plp'
    };
    if (isQuickShopButton) {
      e.preventDefault();
      logAmplitude('Clicked Product Quick View', eventProps);
      setActiveQuickshopID(+hit.objectID);
    } else {
      updateAlgoliaSearchParams({ objectID, queryID });
      insights('clickedObjectIDsAfterSearch', params);
      logAmplitude('Clicked Product Card', eventProps);
      router.push(`/product/${hit.slug}`);
    }
  };

  const onQuickShopClose = () => {
    setActiveQuickshopID(-1);

    if (ref.current) {
      ref.current.scrollIntoView({ block: 'center' });
      ref.current.classList.add('--pulse');
      setTimeout(() => ref.current.classList.remove('--pulse'), 400);
    }
  };

  return (
    <>
      <Card
        ref={ref}
      >
        <Link href={parseSlugFromProductUrl(hit)} passHref>
          <CardAnchor
            onClick={(e) => {
              const insightParams = {
                eventName: 'Product Clicked',
                objectID: hit.objectID,
                // eslint-disable-next-line no-underscore-dangle
                position: hit.__position,
                // eslint-disable-next-line no-underscore-dangle
                queryID: hit.__queryID
              };
              onCardClick(e, insightParams);
            }}
          >
            <ImageWrapper hasSideImage={hit.side_image}>
              <Image
                src={hit.image || `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default-image.jpg`}
                alt={hit.title}
                onError={handleOnImageError}
              />

              {
                hit.side_image && (
                  <Image
                    src={hit.side_image}
                    alt={hit.title}
                    onError={handleOnImageError}
                  />
                )
              }
            </ImageWrapper>

            <QuickShopButton
              data-id="quickshop"
              aria-label={`open "${hit.title}", quick shop`}
              outline
            >
              Quick Shop
            </QuickShopButton>

            <ProductBadge badges={hit.trends} />

            <header>
              <CardTitle>{hit.title}</CardTitle>
              { hit.brand && <CardBrand>{hit.brand}</CardBrand> }
              <CardPrice hit={hit} />
            </header>

          </CardAnchor>
        </Link>

        { isWishlistEnabled && <Wishlist product={hit} /> }
      </Card>

      {
        activeQuickShopID === +hit.objectID && (
          <QuickShop
            slug={hit.slug}
            onQuickShopClose={onQuickShopClose}
            // eslint-disable-next-line no-underscore-dangle
            position={hit.__position}
            insights={insights}
          />
        )
      }
    </>
  );
};

ProductCard.defaultProps = {
  isWishlistEnabled: true
};

ProductCard.propTypes = {
  hit: PropTypes.object.isRequired,
  isWishlistEnabled: PropTypes.bool,
  activeQuickShopID: PropTypes.number.isRequired,
  setActiveQuickshopID: PropTypes.func.isRequired,
  insights: PropTypes.func.isRequired
};

export default connectHitInsights(aa)(ProductCard);
